import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { useEnqueueEnrichment } from '@/hooks/useEnqueueEnrichment';
import { useUsageLimits } from '@/hooks/useBillingAndUsage';
import { limitExceededMessage, toFriendlyLimitError } from '@/lib/limitErrors';
import {
  MAX_REFRESHES_PER_DAY,
  getRefreshLimits,
  recordRefreshUsage,
  validateAndFetchProfilesInBatches,
  persistRefreshedProfiles,
  enqueueMetricsBatches,
  type RefreshProgress,
  type UseInfluencerRefreshReturn,
  type DbInfluencer,
} from './influencer-refresh/index';

export type { RefreshProgress };

/**
 * Hook that encapsulates selected influencer data refresh operations.
 *
 * Manages its own loading state, batch progress, and localStorage rate-limiting.
 */
export function useInfluencerRefresh(): UseInfluencerRefreshReturn {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { enqueueJob, isEnqueueing } = useEnqueueEnrichment();
  const { isAtLimit } = useUsageLimits();
  const [isPolling, setIsPolling] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState<RefreshProgress | null>(null);

  const { canRefreshSelected, refreshCount } = getRefreshLimits();

  /**
   * Refresh selected influencer profile data and metrics in batches.
   * Rate-limited to MAX_REFRESHES_PER_DAY per day.
   */
  const handleSelectedRefresh = useCallback(
    async (selectedInfluencerIds: string[]): Promise<boolean> => {
      if (selectedInfluencerIds.length === 0) return false;

      if (isAtLimit('profile_refreshes')) {
        toast({
          title: 'Profile refresh limit reached',
          description: limitExceededMessage('profile_refreshes'),
          variant: 'destructive',
        });
        return false;
      }

      setIsPolling(true);
      setRefreshProgress({ processedCount: 0, totalCount: selectedInfluencerIds.length });

      try {
        const limits = getRefreshLimits();
        if (!limits.canRefreshSelected) {
          throw new Error(
            `You've reached the daily refresh limit of ${MAX_REFRESHES_PER_DAY} refreshes. You can refresh influencer data again tomorrow.`
          );
        }

        // ── Phase 1: Fetch selected influencers from DB ───────────────────
        const { data: rawInfluencers, error: dbError } = await supabase
          .from('influencers')
          .select('id, handle, instagram_pk')
          .in('id', selectedInfluencerIds);

        if (dbError) throw dbError;

        if (!rawInfluencers || rawInfluencers.length === 0) {
          throw new Error("We couldn't find the selected influencers. Please refresh the page and try again.");
        }

        const dbInfluencers = rawInfluencers as DbInfluencer[];

        // ── Phase 2: Batch profile validation & fetch ─────────────────────
        const { profilesByInfluencerId, failedHandles, failureReasons } =
          await validateAndFetchProfilesInBatches(
            dbInfluencers,
            (processedCount) => {
              setRefreshProgress({
                processedCount,
                totalCount: dbInfluencers.length,
              });
            },
            () => {
              queryClient.invalidateQueries({ queryKey: ['influencers'] });
            }
          );

        // ── Phase 3: Persist fresh profile updates ────────────────────────
        const {
          succeededIds,
          metricReadyInfluencerIds,
          failedHandles: allFailedHandles,
          failureReasons: allFailureReasons,
        } = await persistRefreshedProfiles(
          dbInfluencers,
          profilesByInfluencerId,
          failedHandles,
          failureReasons
        );

        queryClient.invalidateQueries({ queryKey: ['influencers'] });

        // ── Phase 4: Enqueue background metrics enrichment ────────────────
        const metricTargets = dbInfluencers
          .filter((influencer) => metricReadyInfluencerIds.has(influencer.id))
          .map((influencer) => ({ id: influencer.id, handle: influencer.handle }));

        await enqueueMetricsBatches(metricTargets, enqueueJob);

        // ── Phase 5: Record rate-limit usage ──────────────────────────────
        const newCount = recordRefreshUsage();
        queryClient.invalidateQueries({ queryKey: ['influencers'] });

        // ── Phase 6: Show summary feedback toast ──────────────────────────
        if (succeededIds.length === 0) {
          if (metricTargets.length > 0) {
            toast({
              title: 'Refresh Partial',
              description: `We couldn't update the profile details for the selected influencers, but their metrics were refreshed successfully. Remaining refreshes today: ${MAX_REFRESHES_PER_DAY - newCount}.`,
              variant: 'destructive',
            });
            return true;
          }

          toast({
            title: 'Refresh Failed',
            description: `We couldn't refresh the selected influencer profiles. Please try again later. Remaining refreshes today: ${MAX_REFRESHES_PER_DAY - newCount}.`,
            variant: 'destructive',
          });
          return false;
        }

        const failedSuffix =
          allFailedHandles.length > 0
            ? ` ${allFailedHandles.length} profile(s) failed.`
            : '';

        toast({
          title: 'Refresh Complete',
          description: `Refreshed ${succeededIds.length} of ${dbInfluencers.length} profile(s).${failedSuffix} Remaining refreshes today: ${MAX_REFRESHES_PER_DAY - newCount}.`,
        });

        return true;
      } catch (err: unknown) {
        const errMsg = err instanceof Error ? err.message : String(err);
        toast({
          title: 'Refresh Failed',
          description: toFriendlyLimitError(err, errMsg || "We couldn't start the refresh. Please try again."),
          variant: 'destructive',
        });
        return false;
      } finally {
        setIsPolling(false);
        setRefreshProgress(null);
      }
    },
    [enqueueJob, isAtLimit, queryClient, toast]
  );

  /**
   * Refresh only metrics for a single influencer using stored Instagram PK.
   */
  const handleSingleMetricsRefresh = async (influencerId: string): Promise<boolean> => {
    if (!influencerId) return false;

    if (isAtLimit('profile_refreshes')) {
      toast({
        title: 'Profile refresh limit reached',
        description: limitExceededMessage('profile_refreshes'),
        variant: 'destructive',
      });
      return false;
    }

    setIsPolling(true);
    try {
      const limits = getRefreshLimits();
      if (!limits.canRefreshSelected) {
        throw new Error(
          `You've reached the daily refresh limit of ${MAX_REFRESHES_PER_DAY} refreshes. You can refresh influencer data again tomorrow.`
        );
      }

      const { data: dbInfluencer, error: dbError } = await supabase
        .from('influencers')
        .select('id, handle')
        .eq('id', influencerId)
        .single();

      if (dbError) throw dbError;

      if (!dbInfluencer) {
        throw new Error("We couldn't find the selected influencers. Please refresh the page and try again.");
      }

      const result = await enqueueJob([dbInfluencer.id], [dbInfluencer.handle]);

      if (!result) {
        throw new Error("We couldn't update the influencer metrics right now. Please try again.");
      }

      const newCount = recordRefreshUsage();
      queryClient.invalidateQueries({ queryKey: ['influencers'] });

      toast({
        title: 'Metrics Refresh Complete',
        description: `Refreshed metrics for @${dbInfluencer.handle}. Remaining refreshes today: ${MAX_REFRESHES_PER_DAY - newCount}.`,
      });

      return true;
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : String(err);
      toast({
        title: 'Refresh Failed',
        description: toFriendlyLimitError(err, errMsg || "We couldn't refresh the influencer metrics. Please try again."),
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsPolling(false);
    }
  };

  return {
    isPolling,
    isEnqueueing,
    canRefreshSelected,
    selectedCount: refreshCount,
    maxSelectedRefreshes: MAX_REFRESHES_PER_DAY,
    refreshProgress,
    handleSelectedRefresh,
    handleSingleMetricsRefresh,
  };
}
